import React, {
  useEffect, useMemo, useRef, useState,
} from "react";

import { pdf } from "@react-pdf/renderer";
import { isEmpty, pick } from "lodash";
import moment from "moment";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";

import { SPEC_FLAGS_MAP, SUBMISSION_SPEC_FLAGS_MAP } from "../../../Common/constants/SpecFlagConstants";
import DatePicker from "../../../Common/DatePicker/DatePicker";
import PdfPreviewModal from "../../../Common/UIComponents/PdfPreviewModal";
import SchedulingPlanPDF from "./SchedulingPlanPDF";

import { getFormattedDaysAgoString } from "../../../Common/DatePicker/utils";

import { getEnvironmentFields } from "../../../../actions/envAnalytics";
import { getEnvSchedule } from "../../../../actions/envCalendar";

import { ReactComponent as CalendarIcon } from "../../../../assets/images/environment/CalendarEnv.svg";
import "./ExportModal.css";

const getSampleStatusLabel = (specsFlag) => {
  if (SUBMISSION_SPEC_FLAGS_MAP[specsFlag]) {
    return SUBMISSION_SPEC_FLAGS_MAP[specsFlag].display_name;
  }
  if (SPEC_FLAGS_MAP[specsFlag]) {
    return SPEC_FLAGS_MAP[specsFlag].altLabel ?? SPEC_FLAGS_MAP[specsFlag].label;
  }
  return SPEC_FLAGS_MAP.invalid.label;
};

export default function ExportModal({
  title = "Export Scheduling Plan",
  onClose,
  defaultDateRange,
  allowClear = true,
  submissionCategory = "",
  specFilterValues = [],
}) {
  const initialDateRange = getFormattedDaysAgoString(
    defaultDateRange.dateFromObj,
    defaultDateRange.dateToObj,
  );
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfFileName, setPdfFileName] = useState("");
  const [groupedSubmissiondData, setGroupedSubmissiondData] = useState([]);
  const [dateSelected, setDateSelected] = useState(initialDateRange);
  const [prevDateSelected, setPrevDateSelected] = useState(initialDateRange);
  // const [linkPatternFields, setLinkPatternFields] = useState([]);
  const [sampleIDFields, setSampleIDFields] = useState([]);
  const [markedDates, setMarkedDates] = useState([]);
  const [linkPatternDelimiter, setLinkPatternDelimiter] = useState(null);
  const controller = useRef();

  /**
   * Group data sample wise
   * @param {*} data
   */
  const groupData = (data) => {
    if (!data || isEmpty(data.scheduled_submission)) {
      setGroupedSubmissiondData([]);
      return;
    }
    const groupedData = [];
    (data.scheduled_submission ?? []).forEach((submission) => {
      const samples = submission.samples_list.map((s) => {
        const { samples_list, ...rest } = submission;
        const fields = sampleIDFields.map(((field) => field.json_field));
        const sampleidfields = pick(s, fields);
        const testListStr = s.test_list.map((t) => t.Test).join(", ");
        return {
          ...rest,
          sample: {
            ...s,
            ...sampleidfields,
            testListStr,
          },
        };
      });
      groupedData.push(...samples);
    });
    setGroupedSubmissiondData(groupedData);
  };

  /**
   * Calls environmentfields/ api
   */
  const apiGetEnvFields = async () => {
    const result = await getEnvironmentFields();

    if (result && result.success) {
      const {
        fields, index0, delimiter, link_pattern_fields, sampleid_fields, link_pattern,
      } = result;
      const regex = new RegExp(delimiter, "g");
      const linkfields = link_pattern_fields.map((f) => f.split(regex)).flat();
      const sampleidfields = sampleid_fields.map((f) => f.split(regex)).flat();
      return {
        allFields: fields, index0, delimiter, linkPatternFields: linkfields, sampleIDFields: sampleidfields, link_pattern,
      };
    }

    return {
      allFields: [], index0: "", delimiter: "", linkPatternFields: [], sampleIDFields: [], link_pattern: "",
    };
  };

  /**
   * Calls envschedule/ api
   * @param {Date} start start date
   * @param {Date} end end date
   */
  const apiGetEnvSchedule = async (start = "", end = "") => {
    const abortController = new AbortController();
    if (controller.current) {
      controller.current.abort();
    }
    controller.current = abortController;
    const specFilters = specFilterValues.join(",");
    const result = await getEnvSchedule(start, end, specFilters, submissionCategory, controller.current.signal);

    let schedulingData = [];
    if (controller.current?.signal.aborted) {
      schedulingData = [];
    }

    if (result && result.success) {
      const data = result.result;
      if (Array.isArray(data) && data.length === 0) {
        schedulingData = [];
      }
      schedulingData = data;
    }
    controller.current = null;
    return schedulingData;
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      const envFields = await apiGetEnvFields();

      // const fieldMap = new Map(envFields.allFields.map((field) => [field.json_field, field]));
      // const linkFields = envFields.linkPatternFields.map((field_name) => fieldMap.get(field_name));

      const sampleIDCols = envFields.allFields.filter((field) => envFields.sampleIDFields.includes(field.json_field));

      setSampleIDFields(sampleIDCols);
      // setLinkPatternFields(linkFields);
      setLoading(false);
      try {
        setLinkPatternDelimiter(envFields.delimiter ? new RegExp(envFields.delimiter, "g") : /,/g);
      } catch {
        setLinkPatternDelimiter(/,/g);
      }
    })();
  }, []); // eslint-disable-line

  useEffect(() => {
    const fetchInitial = async () => {
      const { dateFromString, dateToString } = defaultDateRange;
      const schedulingData = await apiGetEnvSchedule(dateFromString, dateToString);
      groupData(schedulingData);
      setMarkedDates(schedulingData.scheduled_submission_dates ?? []);
    };
    if (sampleIDFields.length && linkPatternDelimiter) {
      setLoading(true);
      fetchInitial().finally(() => setLoading(false));
    }
  }, [sampleIDFields, linkPatternDelimiter, defaultDateRange]); // eslint-disable-line

  /**
   * Handle date picker change event
   * @param {Date} startDate start date
   * @param {Date} endDate end date
   */
  const handleDatePickerChange = async (startDate, endDate) => {
    const start = startDate ? moment(startDate).format("YYYY-MM-DD") : "";
    const end = endDate ? moment(endDate).format("YYYY-MM-DD") : "";
    setLoading(true);
    const schedulingData = await apiGetEnvSchedule(start, end);
    groupData(schedulingData);
    setMarkedDates(schedulingData.scheduled_submission_dates ?? []);
    setLoading(false);
  };

  const getExcelFileName = () => {
    const timeStamp = moment().unix();
    return `Scheduling_Report_${timeStamp}.xlsx`;
  };

  const buildTable = () => {
    const headers = [
      "Schedule Date",
      "Submission Name",
      ...sampleIDFields.map((field) => field.title_field),
      "Tests",
      "Status",
    ];
    const rows = groupedSubmissiondData.map((submission) => ([
      moment(submission.event_date).format("MM/DD/YYYY"),
      submission.submission_name ?? "",
      ...sampleIDFields.map((field) => submission.sample[field.json_field] ?? ""),
      submission.sample.testListStr ?? "",
      getSampleStatusLabel(submission.sample.specs_flag),
    ]));
    return { headers, rows };
  };

  useEffect(() => {
    if (loading || isEmpty(groupedSubmissiondData)) {
      setPdfBlob(null);
      setPdfLoading(false);
      return undefined;
    }

    let cancelled = false;
    setPdfLoading(true);
    setPdfBlob(null);

    const generatePdf = async () => {
      const fileName = `Scheduling_Report_${moment().unix()}.pdf`;
      const { headers, rows } = buildTable();
      try {
        const blob = await pdf(SchedulingPlanPDF({
          headers,
          rows,
          getFileName: () => fileName,
        })).toBlob();
        if (cancelled) return;
        if (!blob || blob.size === 0) {
          toast.error("Failed to generate pdf");
          setPdfBlob(null);
          return;
        }
        setPdfFileName(fileName);
        setPdfBlob(blob);
      } catch (error) {
        if (cancelled) return;
        toast.error("Failed to generate pdf");
        setPdfBlob(null);
      } finally {
        if (!cancelled) {
          setPdfLoading(false);
        }
      }
    };

    generatePdf();

    return () => {
      cancelled = true;
    };
  }, [groupedSubmissiondData, sampleIDFields, loading]); // eslint-disable-line

  /**
   * Export data to excel file
   * @param {*} data
   */
  const exportToExcel = (data) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Scheduling Report");
    XLSX.writeFile(workbook, getExcelFileName());
  };

  /**
   * Handle download excel file
   */
  const handleDownload = () => {
    if (isEmpty(groupedSubmissiondData)) return;
    const { headers, rows } = buildTable();
    const data = rows.map((row) => headers.reduce((record, header, index) => {
      record[header] = row[index];
      return record;
    }, {}));
    exportToExcel(data);
  };

  const previewFiles = useMemo(() => {
    if (!pdfBlob) return [];
    return [{
      key: "scheduling-plan",
      fileName: pdfFileName || "Scheduling_Report.pdf",
      src: pdfBlob,
    }];
  }, [pdfBlob, pdfFileName]);

  return (
    <PdfPreviewModal
      open
      title={title}
      onClose={onClose}
      files={previewFiles}
      loading={loading || pdfLoading}
      showPrint={false}
      downloadLabel="Download"
      downloadDisabled={loading || isEmpty(groupedSubmissiondData)}
      onDownload={handleDownload}
      errorMessage="No Result Found"
      width="40vw"
      headerExtra={(
        <div className="ExportModal__DateRange">
          <DatePicker
            handleSearchDate={handleDatePickerChange}
            getReportDates={() => markedDates ?? []}
            value={dateSelected}
            onChange={setDateSelected}
            prevValue={prevDateSelected}
            setPrevValue={setPrevDateSelected}
            searchOnClear
            popoverPlacement="bottomLeft"
            allowClear={allowClear}
            datePickerDisplayProps={{
              displayTextPosition: "right",
              displayClassName: "ReportModal__DatePicker",
              customCalendarIcon: <CalendarIcon width={16} height={16} />,
            }}
            maxDate={moment().add(1, "year").endOf("month").format("MM/DD/YYYY")}
          />
        </div>
      )}
    />
  );
}
